import { ChangeDetectionStrategy, Component, inject, input, OnInit } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';

import { AsyncPipe } from '@angular/common';
import { type DeviceHealthState } from '@era/stores';
import { NgbDropdownButtonItem, NgbDropdownItem, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ContextMenuComponent } from '@rocksoft/cdk';
import { environment } from '@rocksoft/environment';
import { DeviceType } from '@rocksoft/locations-sdk';
import { Device, EnvironmentFeatures } from '@rocksoft/shared';
import { RebootDevicesModalContainerComponent } from '../../sub-features/reboot-devices-modal/reboot-devices-modal.container';
import { DeviceGridComponent } from '../device-grid/device-grid.component';
import { selectCurrentLocationSubsetDevicesByHealthState } from '../location-devices/device-health.selector';
import { DevicesSyncModalContainerComponent } from '../modals/devices-sync-modal/devices-sync-modal.container';
import { DeviceLocationSubsetConfig } from './device-location-subset.config';

@Component({
  selector: 'era-devices-location-subset',
  templateUrl: './devices-location-subset.container.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DeviceGridComponent, ContextMenuComponent, NgbDropdownButtonItem, NgbDropdownItem, AsyncPipe],
})
export class DevicesLocationSubsetContainerComponent implements OnInit {
  private readonly deviceStore = inject(Store);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly modalService = inject(NgbModal);

  public readonly config = input.required<DeviceLocationSubsetConfig>();
  public readonly healthState = input.required<DeviceHealthState>();

  public readonly features: EnvironmentFeatures = environment.features;
  public devices$!: Observable<Device[]>;
  public devicesCount$!: Observable<number>;

  private readonly healthState$ = toObservable(this.healthState);

  ngOnInit(): void {
    this.devices$ = this.healthState$.pipe(
      switchMap((healthState) =>
        this.deviceStore.select(
          selectCurrentLocationSubsetDevicesByHealthState(this.config().locationSubset, healthState, Date.now())
        )
      ),
      shareReplay({ bufferSize: 1, refCount: true })
    );
    this.devicesCount$ = this.devices$.pipe(map((devices) => devices.length));
  }

  public itemSelected(device: Device): void {
    if (device.deviceType === DeviceType.Apple && device.udid) {
      void this.router.navigate(['apple', device.udid], { relativeTo: this.route });
    }
  }

  public openRebootRequest(): void {
    const modal = this.modalService.open(RebootDevicesModalContainerComponent, {
      backdrop: 'static',
      centered: true,
      size: 'lg',
    });
    modal.componentInstance.locationSubset = this.config().locationSubset;
  }

  public openSyncModalRequest(): void {
    const modal = this.modalService.open(DevicesSyncModalContainerComponent, {
      backdrop: 'static',
      centered: true,
      size: 'lg',
    });
    modal.componentInstance.locationSubset = this.config().locationSubset;
  }
}
